import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import IncomeModal from "../../core/modals/finance/IncomeModal";
import { fetchIncomeEntries, deleteIncomeEntryThunk } from "../../core/redux/slices/financeSlice";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const IncomeList = () => {
  const dispatch = useDispatch();
  const { income, loading } = useSelector((s) => s.finance);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  useEffect(() => {
    dispatch(fetchIncomeEntries());
  }, [dispatch]);

  const handleSearch = () => dispatch(fetchIncomeEntries());

  // The /api/Financial/income/all endpoint accepts no query params — date filtering is applied client-side.
  const data = useMemo(() => {
    const q = search.toLowerCase();
    return income.filter((e) => {
      const matchSearch = !q || e.remarks?.toLowerCase().includes(q) || e.accountName?.toLowerCase().includes(q);
      const d = e.incomeDate ? e.incomeDate.split("T")[0] : null;
      const matchDate = (!dateFrom || !d || d >= dateFrom) && (!dateTo || !d || d <= dateTo);
      return matchSearch && matchDate;
    });
  }, [income, search, dateFrom, dateTo]);

  const openAdd = () => { setEditRecord(null); setShowModal(true); };
  const openEdit = (rec) => { setEditRecord(rec); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditRecord(null); dispatch(fetchIncomeEntries()); };

  const columns = [
    { title: "Date", dataIndex: "incomeDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—", sorter: (a, b) => (a.incomeDate || "").localeCompare(b.incomeDate || "") },
    { title: "Account Head", dataIndex: "accountName" },
    { title: "Remarks", dataIndex: "remarks", ellipsis: true },
    { title: "Payment Mode", dataIndex: "paymentType", width: 120 },
    { title: "Reference", dataIndex: "incomeEntryNo", width: 150 },
    {
      title: "Amount", dataIndex: "amount", align: "right", width: 120,
      render: fmt,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: "Action", dataIndex: "action", width: 100,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="me-2 p-2 btn btn-sm" onClick={() => openEdit(rec)}><Edit size={15} /></button>
          <button className="p-2 btn btn-sm text-danger"
            onClick={() => confirmDelete(() => dispatch(deleteIncomeEntryThunk(rec.incomeID ?? rec.id)), `income entry on ${rec.incomeDate}`)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Additional Income" subtitle="Record non-billing income entries" onAdd={openAdd} addLabel="Add Income" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} style={{ width: 140 }} />
              </div>
              <button className="btn btn-sm btn-primary" onClick={handleSearch}>Search</button>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search description or account..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <IncomeModal show={showModal} onClose={closeModal} editRecord={editRecord} />
    </div>
  );
};

export default IncomeList;
